import axios from 'axios';
import debug from './debug.js';

/* ***********************************
Utils singleton
************************************ */

const Utils = {
  /**
    Variable type check
   */
  hasValue(va) {
    if (typeof va === 'undefined') return false;
    if (va == null) return false;
    return true;
  },
  isArray(va) {
    if (typeof va === 'undefined') return false;
    return Object.prototype.toString.call(va) === Object.prototype.toString.call([]);
  },
  isObject(va) {
    if (typeof va === 'undefined') return false;
    return Object.prototype.toString.call(va) === Object.prototype.toString.call({});
  },
  isString(va) {
    if (typeof va === 'undefined') return false;
    return Object.prototype.toString.call(va) === Object.prototype.toString.call('');
  },
  isNumber(va) {
    if (typeof va === 'undefined') return false;
    return Object.prototype.toString.call(va) === Object.prototype.toString.call(1);
  },
  isFunction(va) {
    if (typeof va === 'undefined') return false;
    return Object.prototype.toString.call(va) === Object.prototype.toString.call(() => { });
  },


  /* Vue compatible array manipulators */

  /**
   * Empty an array
   */
  emptyArray(arr) {
    arr.splice(0, arr.length);
  },

  /**
   * Prepends an array with values from another array; preserves order of items
   */
  prependArray(arr, withArr) {
    for (let i = withArr.length - 1; i >= 0; i -= 1) {
      arr.unshift(withArr[i]);
    }
  },

  /**
   * Prepends an array with values from another array (in reverse order)
   */
  prependArrayReverse(arr, withArr) {
    for (let i = 0; i < withArr.length; i += 1) {
      arr.unshift(withArr[i]);
    }
  },

  /**
   * Removes an element from an array (of objects) by using a paroperty and a value
   */
  removeElementByProp(arr, p, v) {
    if (!Utils.hasValue(p) || !Utils.isString(p) || (p === '')) return;
    if (typeof v === 'undefined') return;

    for (let i = 0; i < arr.length; i += 1) {
      if ((p in arr[i]) && (arr[i][p] === v)) {
        arr.splice(i, 1);
        return;
      }
    }
  },

  /**
   * Serialize form
   */
  serialize(form) {
    if (!form || form.nodeName !== 'FORM') {
      return;
    }
    let i;
    let j;
    const q = {};
    for (i = form.elements.length - 1; i >= 0; i -= 1) {
      if (form.elements[i].name === '') {
        continue; // eslint-disable-line
      }
      switch (form.elements[i].nodeName) {
        case 'INPUT':
          switch (form.elements[i].type) {
            case 'text':
            case 'hidden':
            case 'password':
            case 'button':
            case 'reset':
            case 'submit':
              q[form.elements[i].name] = form.elements[i].value;
              break;
            case 'checkbox':
            case 'radio':
              if (form.elements[i].checked) {
                q[form.elements[i].name] = form.elements[i].value;
              }
              break;
            case 'file':
              break;
          }
          break;
        case 'TEXTAREA':
          q[form.elements[i].name] = form.elements[i].value;
          break;
        case 'SELECT':
          switch (form.elements[i].type) {
            case 'select-one':
              q[form.elements[i].name] = form.elements[i].value;
              break;
            case 'select-multiple':
              for (j = form.elements[i].options.length - 1; j >= 0; j = j - 1) {
                if (form.elements[i].options[j].selected) {
                  q[form.elements[i].name] = form.elements[i].options[j].value;
                }
              }
              break;
          }
          break;
        case 'BUTTON':
          switch (form.elements[i].type) {
            case 'reset':
            case 'submit':
            case 'button':
              q[form.elements[i].name] = form.elements[i].value;
              break;
          }
          break;
      }
    }
    return q;
  },
};


/** ***********************************
EventBus class
*************************************/

/**
 * Create a new EventBus instance.
 */
function EventBus() {
  this.listeners = {};
  this.listenerId = 0;
}


/**
 * Add an event listener for a specified event
 */
EventBus.prototype.on = function (event, listener) {
  if (!(event in this.listeners)) {
    this.listeners[event] = [];
  }

  let lid = ++this.listenerId;

  this.listeners[event].push(lid, listener);

  return lid;
};


/**
 * Emit an event
 */
EventBus.prototype.emit = function (event, data, parms) {
  if (!(event in this.listeners)) return 0;

  for (let i = 0; i < this.listeners[event].length; i += 2) {
    this.listeners[event][i + 1](data, event, parms);
  }

  return this.listeners[event].length / 2;
};


/**
 * Remove an event listener for a specified event
 */
EventBus.prototype.remove = function (event, listener) {
  if (typeof event === 'undefined') {
    this.listeners = {};
    return 1;
  }

  if (!(event in this.listeners)) return 0;

  if (typeof listener === 'undefined') {
    delete this.listeners[event];
    return 1;
  }

  if (Utils.isNumber(listener)) {
    for (let i = 0; i < this.listeners[event].length; i += 2) {
      if (this.listeners[event][i] == listener) {
        this.listeners[event].splice(i, 2);
        return 1;
      }
    }

    return 0;
  } else if (Utils.isFunction(listener)) {
    for (let i = 0; i < this.listeners[event].length; i += 2) {
      if (this.listeners[event][i + 1] == listener) {
        this.listeners[event].splice(i, 2);
        return 1;
      }
    }

    return 0;
  }

  debug("Don't know how to handle argument ", listener);

  return 0;
};

const evbus = new EventBus();


/*************************************
WebSocket class
************************************ */

/**
 * WebSocket status constants
 */
let WSStatus = {
  CLOSED: 1 << 0, // socket is closed
  OPENING: 1 << 1, // socket is opening connection to server
  OPEN: 1 << 2, // socket has opened connection to server
  CONNECTED: 1 << 3, // initial 'status' packet received from server. Socket is now connected and usable
};


/**
 * WebSocket authentication status constants
 */
let WSAuthStatus = {
  NOT_AUTHENTICATED: 1 << 4, // socket is not authenticated
  AUTHENTICATING: 1 << 5, // socket authentication in progress
  AUTHENTICATED: 1 << 6, // socket is authenticated server side
  SHOULD_AUTHENTICATE: 1 << 7, // socket should authenticate itseld after it is connected
};


/**
 * Request transport types
 */
let RequestTransport = {
  WS: 1 << 0, // execute request only over websocket (if the websocket is not connected, an error will be reported)
  XHR: 1 << 1, // execute request only over AJAX
};


/**
 * Create a new WebSocket (WS) instance.
 */
function WS(url, evbus) {
  this.ws = null;
  this.wssid = null;
  this.eventBus = null;
  this.authTicket = null;
  this.url = '';
  this.status = WSStatus.CLOSED;
  this.authStatus = WSAuthStatus.NOT_AUTHENTICATED;
  this.requestCallbacks = {};
  this.currentCallbackId = 1;
  this.pingPongTimeout = 0;
  this.buffer = [];
  this.reconnect = 0;
  this.forceClose = false;
  this.defaultCb = null;

  if (typeof evbus !== 'undefined') this.eventBus = evbus;
  if (typeof url !== 'undefined') this.connect(url);
}


/**
 * Connect to url
 * eg. wss://securitycoded.com:443/v1/
 *
 * @param {string} url
 */
WS.prototype.connect = function (url) {
  let _this = this;

  if (this.ws && this.ws.readyState == WebSocket.OPEN) {
    debug('A connection is already established. Close it first.');
    return false;
  }

  if (typeof url !== 'undefined') this.url = url;

  if (!this.url) {
    debug('A connect URL must be specified');
    return false;
  }

  let curl = this.url;

  if (this.wssid) curl += '?wssid=' + this.wssid;

  if (process.env.NODE_ENV === 'development') {
    if (curl.indexOf('?') > -1) {
      curl += '&web=1';
    } else {
      curl += '?web=1';
    }
  }

  debug('Connecting to ' + curl);

  let websocket = new WebSocket(curl);

  websocket.onopen = function (ev) {
    if (_this.eventBus) _this.eventBus.emit('ws.open', ev);

    _this.status = WSStatus.OPEN;

    if (_this.reconnect) {
      clearInterval(_this.reconnect);
      _this.reconnect = 0;
    }

    //_this.pingPongTimeout = setInterval(() => {
    //  _this.sendRequest("PING", null, null, null, null, RequestTransport.WS);
    //}, 120000);

    if (_this.buffer.length > 0) {
      let i = 0;
      for (i = 0; i < _this.buffer.length; i++) {
        _this.sendRequestRaw(_this.buffer[i]);
      }
      _this.buffer = [];
    }
    if (_this.eventBus) _this.eventBus.emit('ws.opened', ev);
  };

  websocket.onerror = function (ev) {
    if (_this.eventBus) _this.eventBus.emit('ws.error', ev);

    _this.status = WSStatus.CLOSED;

    debug('onerror', ev);

    clearTimeout(_this.pingPongTimeout);
  };

  websocket.onclose = function (ev, data) {
    if (_this.eventBus) _this.eventBus.emit('ws.close', ev, data);

    _this.status = WSStatus.CLOSED;

    debug('onclose', ev, data);

    clearInterval(_this.pingPongTimeout);

    _this.ws = null;

    if (_this.forceClose) return;

    if (!_this.forceClose) {
      if (!_this.reconnect) {
        debug('Setting up re-connect');
        // _this.reconnect = setInterval(function() {
        setTimeout(() => {
          _this.connect();
        }, 5 * 1000); // try to reconnect after 5 seconds; this may be dangerous having lots of clients trying to reconnect every 5 seconds
      }
    } else {
      if (_this.reconnect) {
        clearInterval(_this.reconnect);
        _this.reconnect = 0;
      }

      _this.forceClose = false;
    }
  };

  websocket.onmessage = function (message) {
    if (_this.eventBus) _this.eventBus.emit('ws.message', message);

    debug('Message = ', message);

    try {
      let data = JSON.parse(message.data);
      _this.listener(data);
    } catch (e) {
      debug('Failed to decode JSON message: ', e, message);
    }
  };

  this.ws = websocket;
  this.status = WSStatus.OPENING;

  return true;
};


/**
 * Disconnect from the server
 */
WS.prototype.close = function () {
  this.forceClose = true;
  if (this.ws) {
    this.ws.close();
  }
};


/**
 * Request websocket authentication
 * !! IMPORTANT !!
 * Must be called after a successfull AJAX login
 */
WS.prototype.authenticate = function (is_ws) {
  let _this = this;

  if (typeof is_ws === 'undefined') is_ws = 1;

  if (is_ws) {
    if (_this.eventBus) _this.eventBus.emit('ws.status', {
      authenticated: true
    });
    this.authStatus = WSAuthStatus.AUTHENTICATED;
    return;
  }

  // if the websocket is connected (there is a difference from 'opened' - we have received the
  // inital 'status' packet from the server)
  if (this.status == WSStatus.CONNECTED) {
    // if the socket is not authenticated
    if (this.authStatus & WSAuthStatus.NOT_AUTHENTICATED) {
      // perform authentication now
      if (this.authStatus & WSAuthStatus.AUTHENTICATING) return;

      // check if there is a valid ticket
      if (this.authTicket == null) return 0;

      // mark websocket as authenticating
      this.authStatus |= WSAuthStatus.AUTHENTICATING;

      // send authenticate request
      this.sendRequest('authenticate', null, {
        ticket: this.authTicket
      },
        (pk) => {
          if (_this.eventBus) _this.eventBus.emit('ws.status', pk);

          if (typeof pk.authenticated != 'undefined' && pk.authenticated) {
            _this.authStatus = WSAuthStatus.AUTHENTICATED;
          } else {
            _this.authStatus = WSAuthStatus.NOT_AUTHENTICATED;
          }
        },
        (error) => { // eslint-disable-line
          _this.authStatus = WSAuthStatus.NOT_AUTHENTICATED;
        },
        RequestTransport.WS
      );

      // reset the authentication ticket. If connection drop we'll authenticate the websocket from cookie
      this.authTicket = null;
    }
  }

  // if the socket is opening/opened the connection (the 'status' packet has not arrived)
  else if ((this.status == WSStatus.OPEN) || (this.status == WSStatus.OPENING)) {
    // signal the socket that once it is connected, if not authenticated (flag received in the initital
    // 'status' packet) it should authenticate itself
    this.authStatus |= WSAuthStatus.SHOULD_AUTHENTICATE;
  }

  return 1;
};


/**
 * Clear wesocket authentication
 * !! IMPORTANT !!
 * Must be called after a successful logout (either via AJAX or vie websocket)
 */
WS.prototype.clearAuthentication = function (is_ws) {
  let _this = this; // eslint-disable-line

  // check that the websocket is authenticated
  if (!(this.authStatus & WSAuthStatus.AUTHENTICATED)) return;

  if (typeof is_ws === 'undefined') is_ws = 1;

  // check if we have to send the request or just set the flag
  if (is_ws) {
    this.authStatus = WSAuthStatus.NOT_AUTHENTICATED;
    return;
  }

  // send an un-authenticate request
  /*
  this.ws.sendRequest('unauthenticate', null, null,
    (result) => {
      if ('cookies' in result) {
        for (var i = 0; i < result.cookies.length; i++) {
          debug("Setting cookie ", result.cookies[i]);
          document.cookie = result.cookies[i];
        }
      }

      _this.authStatus = WSAuthStatus.NOT_AUTHENTICATED;
    },
    (error) => {
    },
    RequestTransport.WS
  );
  */
};


/**
 * Received a message from the server. See what is it about
 *
 * @param {object} data
 */
WS.prototype.listener = function (pk) {
  debug('WS recv = ', pk);

  let handled = 0;

  if (pk && pk.type) {
    if (pk.type === 'status') {
      this.status = WSStatus.CONNECTED;

      if (this.eventBus) this.eventBus.emit('ws.connected', pk);

      this.wssid = pk.wssid;

      debug('WSSID = ', pk.wssid);

      if (pk.authenticated) {
        this.authStatus = WSAuthStatus.AUTHENTICATED;
      } else {
        this.authStatus &= ~WSAuthStatus.AUTHENTICATED;
        this.authStatus |= WSAuthStatus.NOT_AUTHENTICATED;
        if (this.authStatus & WSAuthStatus.SHOULD_AUTHENTICATE) this.authenticate();
      }

      if (this.eventBus) this.eventBus.emit('ws.status', pk);
    } else if (pk.type === 'notification') {
      if (this.eventBus) {
        debug(`ws.notification.${pk.data.type}`, pk.data.payload)
        this.eventBus.emit(`ws.notification.${pk.data.type}`, pk.data.payload);
      }

      handled = 1;
    } else if (pk.callbackid) {
      if (this.requestCallbacks[pk.callbackid]) {
        let firstPacket = this.requestCallbacks[pk.callbackid].firstPacket;
        this.requestCallbacks[pk.callbackid].firstPacket = 0;

        let complete = 'complete' in pk;

        if (('success' in pk) && (pk.success)) {
          if (typeof this.requestCallbacks[pk.callbackid].cb.resolve !== 'undefined') {
            this.requestCallbacks[pk.callbackid].cb.resolve(pk.data, firstPacket, complete, 1);
          }
        } else {
          if (typeof this.requestCallbacks[pk.callbackid].cb.reject !== 'undefined') {
            this.requestCallbacks[pk.callbackid].cb.reject(pk.data);
          }
          complete = 1;
        }

        if (complete) {
          debug('Transaction complete. Deleting callback ' + pk.callbackid);
          delete this.requestCallbacks[pk.callbackid];
        } else {
          debug('Transaction not complete. Waiting for more data');
        }

        handled = 1;
      } else {
        debug('Callback ' + pk.callbackid + ' not found');
      }
    } else {
      debug('Data has no callbackid');
    }
  }

  if (!handled && (this.defaultCb != null)) {
    this.defaultCb(pk);
  }
};


/**
 * Sends a request to the server
 *
 * @param {type} type
 * @param {string} url
 * @param {object} data
 */
WS.prototype.sendRequestPromise = function (type, url, data, noCallback) {
  let _this = this;

  return new Promise(((resolve, reject) => {
    var callbackId = 0;

    if (!noCallback) {
      callbackId = _this.currentCallbackId++;
    }

    var request = {
      type: type,
      url: url,
      data: data,
      callbackid: callbackId
    };

    if (callbackId) {
      _this.requestCallbacks[callbackId] = {
        time: new Date(),
        cb: {
          resolve: resolve,
          reject: reject
        }
      };
    }

    if (!_this.ws || _this.ws.readyState != WebSocket.OPEN) { // we aren't connected yet so buffer the request
      _this.buffer.push(request);
      return;
    }

    _this.ws.send(JSON.stringify(request));
  }));
};


/**
 * Sends a request to the server
 *
 * @param {type} type
 * @param {string} url
 * @param {object} data
 */
WS.prototype.sendRequest = function (type, url, data, resolve, reject, rt) {
  // by default the request transport is websocket with fallback to AJAX
  if (typeof rt === 'undefined') rt = RequestTransport.WS | RequestTransport.XHR;

  // check if this is an websocket only request
  if (rt == RequestTransport.WS) {
    // if the websocket is not connected and ready for requests, trigger an error and return
    if (this.status != WSStatus.CONNECTED) {
      if (typeof reject !== 'undefined') {
        reject({
          'error': 1,
          'message': 'Service down for maintenance. Please try again later.'
        });
      }

      return;
    }
  }

  // if the caller wants to execute the request using AJAX or if the websocket is not connected
  if ((rt == RequestTransport.XHR) || (this.status != WSStatus.CONNECTED)) {
    // send AJAX request
    return axios[type](url, data)
      .then((result) => {
        debug("AXIOS result = ", result);

        if (typeof result.data === "object" && 'success' in result.data) {
          if (typeof resolve !== 'undefined') {
            let pk = {};
            if (typeof result.data === "object") {
              if ('data' in result.data) {
                pk = result.data.data;
              } else {
                pk = result.data;
              }
            }
            resolve(pk, 1, 1, 0);
          }
        } else {
          if (typeof reject !== 'undefined') {
            let pk = {};
            if (typeof result.data === "object") {
              if ('data' in result.data) {
                pk = result.data.data;
              } else {
                pk = result.data;
              }
            }
            reject(pk, 0);
          }
        }
      })
      .catch((err) => {
        debug("Error = ", err);
        if (typeof reject !== 'undefined') {
          let pk = {};
          if (err.response) {
            if (typeof err.response === "object") {
              if ('data' in err.response && typeof err.response.data === "object" && 'data' in err.response.data) {
                pk = err.response.data.data;
              } else if ('data' in err.response) {
                pk = err.response.data;
              }
            }
          }
          reject(pk);
        }
      });
  }

  // execute request using websocket

  // callbackId == 0 means that even if we get a response, it will be discarded
  let callbackId = 0;

  // generate a callbackId only if one or both of the success/error callbacks is defined
  if ((typeof resolve !== 'undefined') || (typeof reject !== 'undefined')) {
    callbackId = this.currentCallbackId++;
  }

  // request packet
  let request = {
    type,
    url,
    data,
    callbackid: callbackId,
  };

  // register callbacks if at least one of them was provided
  if (callbackId) {
    this.requestCallbacks[callbackId] = {
      time: new Date(),
      cb: {
        resolve: resolve,
        reject: reject
      },
      firstPacket: 1
    };
  }

  // buffer the request if the websocket is not connected yet
  if (!this.ws || this.ws.readyState != WebSocket.OPEN) {
    // we aren't connected yet so buffer the request
    this.buffer.push(request);
    return;
  }

  // send request
  this.ws.send(JSON.stringify(request));
};


/**
 * Sends an already build request to the server. Used when trying to send a request but we aren't connected to the server already
 *
 * @param {string} request
 */
WS.prototype.sendRequestRaw = function (request) {
  this.ws.send(JSON.stringify(request));
};


/**
 * Check if the websocket is connected
 */
WS.prototype.isConnected = function () {
  if (this.status & WSStatus.CONNECTED) return 1;
  return 0;
};


export {
  evbus,
  WS,
  Utils
};
